import React, { useEffect, useState, useMemo } from "react";
import {
	Row,
	Col,
	Card,
	Tag,
	Space,
	Typography,
	Button,
	Drawer,
	List,
	message,
	Select,
	Modal,
	Progress,
	Input,
	Checkbox,
} from "antd";
import {
	CloudDownloadOutlined,
	LeftOutlined,
	ReloadOutlined,
	RightOutlined,
	SendOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
	get_agent_client_route_tags,
	get_agent_pos_selection_ids,
	get_agent_pos_with_last_conv,
	get_client_templates,
	get_conversation_messages_page,
	get_conversation_phases,
	get_pos_conversations_page,
	release_conversation_to_agent,
	send_template_to_pos,
	send_human_conversation_message,
	sync_client_templates,
	take_over_conversation,
} from "../../../../../api/endpoints/agents";
import AntdSearch from "../../../../common/antd-search/AntdSearch";
import { CONSTANTS } from "../../../../../utils/constants";
import {
	AntdInput,
	AntdTable,
	CardContainer,
	ChatHeader,
	ChatHeaderMeta,
	ChatPanel,
	Circle,
	ComposerBar,
	CompaginationDiv,
	Container,
	ControlBadge,
	ContactListPanel,
	ContactRow,
	ConversationRow,
	ConversationSidebar,
	FilterActions,
	FilterContainer,
	HistoryListViewport,
	HistoryModalBody,
	HistoryPagination,
	InboxShell,
	Inner,
	MessageBubble,
	MessageMeta,
	MessagesViewport,
	PanelPagination,
	SidebarHeader,
	TimelineEvent,
} from "./styles";
import { COLORS, Typo } from "../../../../layout/theme";
import debounce from "lodash/debounce";
import CustomAntdTooltip from "../../../../common/custom-antd-tooltip/CustomAntdTooltip";
import moment from "moment";
import LeadStatusTag from "./components/LeadStatusTags";
import DefaultButton from "../../../../common/antd-buttons/DefaultButton";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";
import SecondaryButton from "../../../../common/antd-buttons/SecondaryButton";

export default function AgentConversations() {
	const { id: clientId } = useParams();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const [poolFilter, setPoolFilter] = useState("ALL");
	const [stateFilter, setStateFilter] = useState("ACTIVE");
	const [phaseFilter, setPhaseFilter] = useState(null);

	const [pageSize] = useState(CONSTANTS.PAGE_LARGE_SIZE);
	const [actualPage, setActualPage] = useState(1);

	const [rows, setRows] = useState([]);
	const [pageInfo, setPageInfo] = useState(null);

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedPos, setSelectedPos] = useState(null);
	const [historyModalOpen, setHistoryModalOpen] = useState(false);

	const [convLoading, setConvLoading] = useState(false);
	const [convs, setConvs] = useState([]);
	const [convPage, setConvPage] = useState(1);
	const [convInfo, setConvInfo] = useState(null);

	const [msgsLoading, setMsgsLoading] = useState(false);
	const [olderMsgsLoading, setOlderMsgsLoading] = useState(false);
	const [msgs, setMsgs] = useState([]);
	const [selectedConversationId, setSelectedConversationId] = useState(null);
	const [msgPage, setMsgPage] = useState(1);
	const [msgInfo, setMsgInfo] = useState(null);
	const messagesViewportRef = React.useRef(null);

	const [phasesInfo, setPhasesInfo] = useState(null);
	const [search, setSearch] = useState("");
	const [sortLastConversation, setSortLastConversation] = useState("PDV");

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [listRefreshTick, setListRefreshTick] = useState(false);
	const [selectAllLoading, setSelectAllLoading] = useState(false);
	const [selectAllTotal, setSelectAllTotal] = useState(0);
	const selectedCount = selectedRowKeys.length;
	const isAllSelected = selectAllTotal > 0 && selectedCount === selectAllTotal;
	const selectedPosMapRef = React.useRef({});

	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [sendProgress, setSendProgress] = useState(0);
	const [sendStatus, setSendStatus] = useState("");
	const [sendDetails, setSendDetails] = useState("");
	const sendTimerRef = React.useRef(null);

	const [templatesLoading, setTemplatesLoading] = useState(false);
	const [templates, setTemplates] = useState([]);
	const [templateMap, setTemplateMap] = useState({});
	const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
	const [bulkToolsOpen, setBulkToolsOpen] = useState(false);
	const [routeTagOptions, setRouteTagOptions] = useState([]);
	const [selectedRouteTag, setSelectedRouteTag] = useState(undefined);
	const [loadingRouteTags, setLoadingRouteTags] = useState(false);
	const [takeoverModalOpen, setTakeoverModalOpen] = useState(false);
	const [takeoverReason, setTakeoverReason] = useState("");
	const [controlActionLoading, setControlActionLoading] = useState(false);
	const [humanMessageText, setHumanMessageText] = useState("");
	const [sendingHumanMessage, setSendingHumanMessage] = useState(false);
	const sendingHumanMessageRef = React.useRef(false);

	const isImageHeaderTemplate = (template) =>
		(template?.headerFormat || "").toUpperCase() === "IMAGE" &&
		!!template?.headerMediaUrl;

	const getPreviewText = (template) => {
		const preview = template?.previewFull || template?.previewBody || "—";
		if (!isImageHeaderTemplate(template)) return preview;

		return (
			preview.replace(/^\s*\[(Imagen|Image)\].*(?:\r?\n|$)/gim, "").trim() ||
			"—"
		);
	};

	const getErrorMessage = (error, fallback) => {
		const data = error?.response?.data;

		if (!data) return fallback;
		if (typeof data === "string") return data;
		if (data.message) return data.message;
		if (data.error) return data.error;
		if (data.title) return data.title;

		try {
			return JSON.stringify(data);
		} catch {
			return fallback;
		}
	};

	const fetchPage = async (search, pg = 1) => {
		try {
			setLoading(true);
			const res = await get_agent_pos_with_last_conv({
				clientId,
				poolFilter,
				search,
				state: stateFilter,
				phase: phaseFilter,
				routeTag: selectedRouteTag,
				sortLastConversation: sortLastConversation,
				page: pageSize,
				actualPage: pg,
			});
			const items = res.items || [];
			setRows(items);
			const newMap = { ...selectedPosMapRef.current };
			items.forEach((r) => {
				newMap[r.agentRouteId] = r.pointOfSaleId;
			});
			selectedPosMapRef.current = newMap;
			setPageInfo(res.paginationInfo || null);
			setActualPage(pg);

			if (!selectedPos && !drawerOpen) {
				const firstContact = items.find(
					(r) => r.pointOfSaleId && r.lastMessagePreview,
				);
				if (firstContact) openDrawer(firstContact);
			}
		} catch (e) {
			console.error(e);
			message.error("No se pudo cargar el listado de PDV.");
		} finally {
			setLoading(false);
		}
	};

	const fetchConversationPhases = async () => {
		try {
			setLoading(true);
			const res = await get_conversation_phases(clientId);
			setPhasesInfo(res.phases);
		} catch (error) {}
	};

	const fetchTemplates = async () => {
		if (!clientId) return;

		try {
			setTemplatesLoading(true);
			const list = await get_client_templates(clientId);
			const normalized = (list || []).map((t) => ({
				...t,
				headerFormat: t.headerFormat ?? t.HeaderFormat,
				headerMediaUrl: t.headerMediaUrl ?? t.HeaderMediaUrl,
			}));

			setTemplates(normalized);

			const map = {};
			normalized.forEach((t) => {
				map[t.name] = t;
			});
			setTemplateMap(map);
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar las plantillas del cliente.");
		} finally {
			setTemplatesLoading(false);
		}
	};

	useEffect(() => {
		fetchPage(search, 1);
		if (clientId) fetchConversationPhases();
		if (clientId) fetchTemplates();
		if (clientId) fetchRouteTags();
		setSelectedRowKeys([]);
		setSelectAllTotal(0);
	}, [
		clientId,
		poolFilter,
		stateFilter,
		phaseFilter,
		selectedRouteTag,
		sortLastConversation,
		listRefreshTick,
	]);

	useEffect(() => {
		return () => {
			debouncedEventHandler.cancel();
		};
	}, []);

	const eventHandler = (e) => {
		const v = e.target.value;
		setSearch(v);
		fetchPage(v, 1);
	};

	const debouncedEventHandler = useMemo(() => debounce(eventHandler, 600), []);

	const columns = useMemo(
		() => [
			{
				title: () => <div style={{ color: COLORS.White }}>Punto de Venta</div>,
				dataIndex: "pdvName",
				render: (v, r) => (
					<Space direction="vertical" size={0}>
						<Space>
							{r.isValidated ? (
								<Circle color={COLORS.Success} />
							) : (
								<CustomAntdTooltip
									width="250px"
									color={COLORS.Secondary}
									tooltipContent={[
										{
											title: "Punto de venta sin validar",
											body: `Click para redireccionar a validaciones`,
										},
									]}
								>
									<Circle
										color={COLORS.Warning}
										onClick={() =>
											navigate(`/admin/laboratoryAgentPool/${clientId}`)
										}
										style={{ cursor: "pointer" }}
									/>
								</CustomAntdTooltip>
							)}
							<Typography.Text strong>{v}</Typography.Text>
						</Space>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{r.address} • {r.province} • {r.location}
						</Typography.Text>
					</Space>
				),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Cuit</div>,
				dataIndex: "cuit",
				width: 140,
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Ultima fase</div>,
				dataIndex: "lastConversationPhase",
				width: 160,
				render: (v, r) =>
					v ? (
						r.lastConversationHasTicket ? (
							<Tag color="volcano">
								{CONSTANTS.AGENT_CONVERSATION["Done"]?.label}
							</Tag>
						) : (
							<Tag color={CONSTANTS.AGENT_CONVERSATION[v]?.color || "blue"}>
								{CONSTANTS.AGENT_CONVERSATION[v]?.label || "Indefinido"}
							</Tag>
						)
					) : (
						<Typography.Text type="secondary">—</Typography.Text>
					),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Lead</div>,
				dataIndex: "leadStatus",
				width: 180,
				render: (v, r) => (
					<LeadStatusTag status={r.leadStatus} reason={r.leadStatusReason} />
				),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Ultimo mensaje</div>,
				key: "lastmsg",
				render: (_, r) => (
					<Space direction="vertical" size={0}>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{r.lastMessageAt
								? moment.utc(r.lastMessageAt).local().format("DD/MM/YYYY HH:mm")
								: "—"}
						</Typography.Text>
						<div
							style={{
								maxWidth: 360,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{r.lastMessagePreview || "—"}
						</div>
					</Space>
				),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Detalles</div>,
				key: "act",
				width: 120,
				render: (_, r) => (
					<Button
						type="link"
						onClick={() => openDrawer(r)}
						disabled={!r.pointOfSaleId || !r.lastMessagePreview}
					>
						Ver conversaciones
					</Button>
				),
			},
		],
		[],
	);

	const openDrawer = async (row) => {
		if (!row.pointOfSaleId) return message.info("PDV no validado.");
		setSelectedPos(row);
		setDrawerOpen(true);
		await loadConversations(row.pointOfSaleId, 1);
	};

	const loadConversations = async (
		pointOfSaleId,
		pg,
		opts = { keepSelection: false, keepMsgPage: false },
	) => {
		try {
			setConvLoading(true);
			const res = await get_pos_conversations_page({
				clientId,
				pointOfSaleId,
				page: CONSTANTS.PAGE_SMALL_SIZE,
				actualPage: pg,
			});
			setConvs(res.items || []);
			setConvInfo(res.paginationInfo || null);
			setConvPage(pg);
			const items = res.items || [];
			const first = items[0];

			const keep =
				opts.keepSelection &&
				selectedConversationId &&
				items.some((x) => x.id === selectedConversationId);

			const nextId = keep ? selectedConversationId : first?.id || null;
			setSelectedConversationId(nextId);

			if (nextId) {
				const nextMsgPage = opts.keepMsgPage ? msgPage : 1;
				await loadMessages(nextId, nextMsgPage);
			} else {
				setMsgs([]);
				setMsgInfo(null);
			}
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar conversaciones.");
		} finally {
			setConvLoading(false);
		}
	};

	const normalizeMessagesForChat = (items) => [...(items || [])].reverse();

	const getMessageKey = (message) =>
		message?.messageId ||
		message?.id ||
		`${message?.created || ""}-${message?.source || message?.role || ""}-${message?.content || ""}`;

	const mergeUniqueMessages = (items) => {
		const seen = new Set();
		return items.filter((item) => {
			const key = getMessageKey(item);
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};

	const afterMessageRender = (callback) => {
		requestAnimationFrame(() => requestAnimationFrame(callback));
	};

	const scrollMessagesToBottom = () => {
		afterMessageRender(() => {
			const el = messagesViewportRef.current;
			if (!el) return;
			el.scrollTop = el.scrollHeight;
		});
	};

	const loadMessages = async (conversationId, pg, mode = "replace") => {
		const isPrepend = mode === "prepend";
		const viewport = messagesViewportRef.current;
		const previousScrollHeight = viewport?.scrollHeight || 0;
		const previousScrollTop = viewport?.scrollTop || 0;

		try {
			if (isPrepend) setOlderMsgsLoading(true);
			else setMsgsLoading(true);

			const res = await get_conversation_messages_page({
				conversationId,
				page: CONSTANTS.PAGE_SMALL_SIZE,
				actualPage: pg,
			});

			const normalizedMessages = normalizeMessagesForChat(res.items);

			setMsgs((prev) =>
				isPrepend
					? mergeUniqueMessages([...normalizedMessages, ...prev])
					: normalizedMessages,
			);
			setMsgInfo(res.paginationInfo || null);
			setMsgPage(pg);

			if (isPrepend) {
				afterMessageRender(() => {
					const el = messagesViewportRef.current;
					if (!el) return;
					el.scrollTop =
						el.scrollHeight - previousScrollHeight + previousScrollTop;
				});
			} else {
				scrollMessagesToBottom();
			}
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar mensajes.");
		} finally {
			if (isPrepend) setOlderMsgsLoading(false);
			else setMsgsLoading(false);
		}
	};

	const loadOlderMessages = async () => {
		if (!selectedConversationId || !msgInfo) return;
		if (msgPage >= msgInfo.totalPages) return;
		await loadMessages(selectedConversationId, msgPage + 1, "prepend");
	};

	const handleDecrement = (state, fetch, set) => {
		if (state.actualPage > 1) {
			const cp = state.actualPage - 1;
			fetch(search, cp);
			set(cp);
		}
	};

	const handleIncrement = (state, fetch, set) => {
		if (state.actualPage < state.totalPages) {
			const cp = state.actualPage + 1;
			fetch(search, cp);
			set(cp);
		}
	};

	const handlePageChange = (e, state, fetch, set) => {
		const selectedPage = parseInt(e.target.value);
		if (
			!isNaN(selectedPage) &&
			selectedPage >= 1 &&
			selectedPage <= state.totalPages
		) {
			fetch(search, selectedPage);
			set(selectedPage);
		} else {
			set(state.actualPage);
		}
	};

	const getColor = (status) => {
		switch (status) {
			case "OK":
				return COLORS.Success;
			case "Warning":
				return COLORS.Warning;
			case "Error":
				return COLORS.Danger;
			default:
				return COLORS.LightGrey;
		}
	};

	const rowSelection = useMemo(() => {
		const mergeKeys = (nextKeys) => setSelectedRowKeys(nextKeys);

		return {
			selectedRowKeys,
			preserveSelectedRowKeys: true,

			onSelect: (record, selected) => {
				const k = record.agentRouteId;

				setSelectedRowKeys((prev) => {
					if (selected) return prev.includes(k) ? prev : [...prev, k];
					return prev.filter((x) => x !== k);
				});
			},

			onSelectAll: (selected, selectedRows, changeRows) => {
				const keysChanged = changeRows.map((r) => r.agentRouteId);

				setSelectedRowKeys((prev) => {
					if (selected) {
						const set = new Set(prev);
						keysChanged.forEach((k) => set.add(k));
						return Array.from(set);
					}
					return prev.filter((k) => !keysChanged.includes(k));
				});
			},

			onChange: mergeKeys,

			getCheckboxProps: (record) => ({
				disabled: !record.pointOfSaleId,
			}),
		};
	}, [selectedRowKeys]);

	const refreshList = () => setListRefreshTick((p) => !p);

	const refreshDrawer = async () => {
		if (!selectedPos?.pointOfSaleId) return;
		await loadConversations(selectedPos.pointOfSaleId, convPage, {
			keepSelection: true,
			keepMsgPage: true,
		});
	};

	const refreshMessagesOnly = async () => {
		if (!selectedConversationId) return;
		await loadMessages(selectedConversationId, 1, "replace");
	};

	const selectedConversation = useMemo(
		() => (convs || []).find((x) => x.id === selectedConversationId) || null,
		[convs, selectedConversationId],
	);

	const getControlMode = (conversation) =>
		conversation?.controlMode === "Human" ? "Human" : "Agent";

	const getControlBadge = (conversation) => {
		const mode = getControlMode(conversation);
		return mode === "Human" ? "En control Humano" : "Agente activo";
	};

	const getMessageSource = (message) => {
		if (message?.isEvent) return "System";
		if (message?.source) return message.source;
		if (message?.role === "user") return "EndUser";
		if (message?.role === "assistant") return "Agent";
		return "System";
	};

	const formatControlDate = (value) =>
		value ? moment.utc(value).local().format("DD/MM/YYYY HH:mm") : "—";

	const getConversationDate = (conversation) =>
		formatControlDate(
			conversation?.created ||
				conversation?.createdAt ||
				conversation?.updatedAt,
		);

	const canTakeOverConversation = (conversation) =>
		!!conversation &&
		getControlMode(conversation) === "Agent" &&
		(!conversation.isFinished || conversation.hasTicket);

	const handleTakeover = async () => {
		if (!selectedConversationId) return;
		const conversationId = selectedConversationId;
		if (!canTakeOverConversation(selectedConversation)) {
			message.warning(
				"Solo se puede tomar una conversación abierta o una cerrada con ticket.",
			);
			return;
		}

		try {
			setControlActionLoading(true);
			await take_over_conversation(
				selectedConversationId,
				takeoverReason || "",
			);
			setTakeoverModalOpen(false);
			setTakeoverReason("");
			await refreshDrawer();
			await loadMessages(conversationId, 1, "replace");
			message.success("Conversación tomada por un humano.");
		} catch (e) {
			console.error(e);
			message.error("No se pudo tomar la conversación.");
		} finally {
			setControlActionLoading(false);
		}
	};

	const confirmReleaseToAgent = () => {
		if (!selectedConversationId) return;

		Modal.confirm({
			title: "Devolver conversación al agente",
			content:
				"El cliente no recibirá un mensaje automático. El agente retomará la conversación en el próximo intercambio.",
			okText: "Release to agent",
			cancelText: "Cancelar",
			onOk: async () => {
				try {
					setControlActionLoading(true);
					await release_conversation_to_agent(selectedConversationId);
					await refreshDrawer();
					message.success("Conversación devuelta al agente.");
				} catch (e) {
					console.error(e);
					message.error("No se pudo devolver la conversación al agente.");
				} finally {
					setControlActionLoading(false);
				}
			},
		});
	};

	const sendHumanMessage = async () => {
		if (sendingHumanMessageRef.current) return;

		const text = humanMessageText.trim();
		if (!selectedConversationId || !text) return;
		const conversationId = selectedConversationId;

		try {
			sendingHumanMessageRef.current = true;
			setSendingHumanMessage(true);
			await send_human_conversation_message(conversationId, text);
			setHumanMessageText("");
			await refreshDrawer();
			await loadMessages(conversationId, 1, "replace");
		} catch (e) {
			console.error(e);
			message.error("No se pudo enviar el mensaje humano.");
		} finally {
			sendingHumanMessageRef.current = false;
			setSendingHumanMessage(false);
		}
	};

	const leadSummary = useMemo(() => {
		const acc = {};
		(rows || []).forEach((r) => {
			const k = r.leadStatus || "—";
			acc[k] = (acc[k] || 0) + 1;
		});
		return acc;
	}, [rows]);

	const estimateSendSeconds = (n) => {
		// backend: sendBatchSize=200, maxConcurrency=4, delayBetweenBatches=0.5s
		// supuestos: cada envío ~0.35s promedio por request
		const perSend = 0.35;
		const concurrency = 4;
		const batch = 200;
		const delayBetweenBatches = 0.5;

		const batches = Math.max(1, Math.ceil(n / batch));
		const sendTime = (n * perSend) / concurrency;
		const delays = Math.max(0, batches - 1) * delayBetweenBatches;

		return Math.ceil((sendTime + delays) * 1.25);
	};

	const startFakeProgress = (total) => {
		if (sendTimerRef.current) clearInterval(sendTimerRef.current);

		const etaSec = estimateSendSeconds(total);
		const startedAt = Date.now();

		setSendProgress(2);
		setSendStatus("Preparando envío…");
		setSendDetails(`Destinatarios: ${total} • Tiempo estimado: ~${etaSec}s`);

		const phases = [
			{ at: 0.05, text: "Preparando envío…" },
			{ at: 0.18, text: "Validando destinatarios…" },
			{ at: 0.35, text: "Chequeando bajas (opt-out)..." },
			{ at: 0.55, text: "Enviando mensajes…" },
			{ at: 0.82, text: "Guardando registros…" },
			{ at: 0.93, text: "Finalizando…" },
		];

		sendTimerRef.current = setInterval(() => {
			const elapsed = (Date.now() - startedAt) / 1000;
			const t = Math.min(1, elapsed / etaSec);

			const eased = 1 - Math.pow(1 - t, 2.4);

			const next = Math.min(97, Math.floor(eased * 97));
			setSendProgress((p) => (next > p ? next : p));

			const pRel = next / 100;
			const phase = phases.reduce(
				(acc, cur) => (pRel >= cur.at ? cur : acc),
				phases[0],
			);
			setSendStatus(phase.text);

			const remaining = Math.max(0, etaSec - elapsed);
			setSendDetails(
				`Destinatarios: ${total} • Restante aprox.: ~${Math.ceil(remaining)}s`,
			);
		}, 250);
	};

	const stopFakeProgress = () => {
		if (sendTimerRef.current) {
			clearInterval(sendTimerRef.current);
			sendTimerRef.current = null;
		}
	};

	useEffect(() => {
		return () => stopFakeProgress();
	}, []);

	const fetchRouteTags = async () => {
		try {
			setLoadingRouteTags(true);

			const res = await get_agent_client_route_tags(clientId);

			setRouteTagOptions(
				(res || []).map((item) => ({
					value: item.value,
					label: item.label,
				})),
			);
		} catch (error) {
			console.error(error);
			message.error("No se pudieron cargar los tags internos.");
		} finally {
			setLoadingRouteTags(false);
		}
	};

	return (
		<Inner>
			<Row gutter={[12, 12]}>
				<Col span={24}>
					{/* <Card> */}
					<Space
						direction="vertical"
						style={{ marginBottom: 12, width: "100%" }}
					>
						<FilterContainer>
							<AntdSearch
								placeholder="Buscar por nombre o CUIT"
								allowClear
								onChange={debouncedEventHandler}
								style={{ width: 280, borderRadius: 15, height: 32 }}
							/>
							<Container>
								<Tag
									color="default"
									style={{ cursor: "pointer", borderRadius: 5 }}
									onClick={() => setPhaseFilter(null)}
								>
									Todos ({pageInfo?.totalRecords})
								</Tag>
								{phasesInfo?.map((p, idx) => (
									<Tag
										key={`${p.phase}_${idx}`}
										color={
											CONSTANTS.AGENT_CONVERSATION[p.phase]?.color || "blue"
										}
										style={{ cursor: "pointer", borderRadius: 5 }}
										onClick={() => {
											setPhaseFilter(p.phase);
											setActualPage(1);
										}}
									>
										{CONSTANTS.AGENT_CONVERSATION[p.phase]?.label || ""} (
										{p.count})
									</Tag>
								))}
							</Container>
							<FilterActions>
								{routeTagOptions && routeTagOptions?.length > 0 && (
									<Select
										allowClear
										showSearch
										placeholder="Filtrar por tag"
										style={{ width: 160 }}
										value={selectedRouteTag}
										onChange={(v) => {
											setSelectedRouteTag(v);
										}}
										options={routeTagOptions}
										loading={loadingRouteTags}
										optionFilterProp="label"
									/>
								)}
								<Button
									type="text"
									size="small"
									icon={<ReloadOutlined />}
									onClick={refreshList}
									loading={loading}
								/>
								<DefaultButton
									onClick={() => setBulkToolsOpen((prev) => !prev)}
									style={{
										height: "32px",
										border: "1px solid grey",
										fontWeight: 400,
									}}
								>
									{bulkToolsOpen
										? "Ocultar controles plantillas"
										: "Mostrar controles plantillas"}
								</DefaultButton>
							</FilterActions>
						</FilterContainer>
						{bulkToolsOpen && (
							<FilterContainer style={{ gap: 6 }}>
								<DefaultButton
									loading={templatesLoading}
									onClick={async () => {
										try {
											setTemplatesLoading(true);
											await sync_client_templates(clientId);
											await fetchTemplates();
											message.success("Plantillas sincronizadas.");
										} catch (e) {
											console.error(e);
											message.error("No se pudo sincronizar plantillas.");
										} finally {
											setTemplatesLoading(false);
										}
									}}
									style={{
										height: "30px",
										border: "1px solid grey",
										fontWeight: 400,
									}}
								>
									Sincronizar <CloudDownloadOutlined />
								</DefaultButton>
								<Select
									placeholder="Plantilla"
									style={{ width: 260 }}
									value={selectedTemplate}
									onChange={(v) => {
										setSelectedTemplate(v);
									}}
									loading={templatesLoading}
									showSearch
									optionFilterProp="label"
									options={(templates || [])
										.filter((t) => !t.isDeletedInMeta)
										.map((t) => ({
											value: t.name,
											label: `${t.displayName}`,
										}))}
								/>
								<DefaultButton
									loading={selectAllLoading}
									style={{
										height: "30px",
										border: "1px solid grey",
										fontWeight: 400,
									}}
									onClick={async () => {
										if (isAllSelected) {
											setSelectedRowKeys([]);
											setSelectAllTotal(0);
											return;
										}

										try {
											setSelectAllLoading(true);

											const res = await get_agent_pos_selection_ids({
												clientId,
												poolFilter,
												search: "",
												state: stateFilter,
												phase: phaseFilter,
											});

											const allKeys = res.agentRouteIds || [];
											setSelectedRowKeys(allKeys);
											setSelectAllTotal(res.total ?? allKeys.length);

											selectedPosMapRef.current = {
												...selectedPosMapRef.current,
												...(res.map || {}),
											};

											if (allKeys.length === 0) {
												message.info(
													"No hay PDV validados para seleccionar con los filtros actuales.",
												);
											}
										} catch (e) {
											console.error(e);
											message.error("No se pudo seleccionar todos los PDV.");
										} finally {
											setSelectAllLoading(false);
										}
									}}
								>
									{isAllSelected ? "Deseleccionar todos" : "Seleccionar todos"}
								</DefaultButton>
								<SecondaryButton
									disabled={!selectedTemplate}
									onClick={() => setTemplatePreviewOpen(true)}
									style={{ height: 30, fontWeight: 400 }}
								>
									Vista previa
								</SecondaryButton>
								<PrimaryButton
									type="primary"
									disabled={!selectedTemplate || selectedRowKeys.length === 0}
									style={{ height: 30, fontWeight: 400 }}
									onClick={async () => {
										if (!selectedTemplate) {
											message.warning("Seleccioná una plantilla.");
											return;
										}
										if (selectedRowKeys.length === 0) {
											message.warning("Seleccioná al menos un punto de venta.");
											return;
										}

										const pointOfSaleIds = selectedRowKeys
											.map((k) => selectedPosMapRef.current[k])
											.filter(Boolean);

										if (pointOfSaleIds.length === 0) {
											message.warning(
												"Los PDV seleccionados no están validados.",
											);
											return;
										}

										try {
											setLoading(true);

											setSendModalOpen(true);
											startFakeProgress(pointOfSaleIds.length);

											const result = await send_template_to_pos({
												clientId,
												templateName: selectedTemplate,
												pointOfSaleIds,
											});

											setSendStatus("¡Listo! Plantilla enviada.");
											setSendDetails(
												result || `Destinatarios: ${pointOfSaleIds.length}`,
											);
											setSendProgress(100);

											message.success(
												result || "Plantilla enviada a los PDV seleccionados.",
											);

											setTimeout(() => {
												stopFakeProgress();
												setSendModalOpen(false);
												setSendProgress(0);
											}, 650);
										} catch (e) {
											console.error(e);
											stopFakeProgress();
											const errorMessage = getErrorMessage(
												e,
												"No se pudo enviar la plantilla.",
											);

											setSendStatus("Ocurrió un error enviando la plantilla.");
											setSendDetails(errorMessage);
											setSendProgress(0);

											message.error(errorMessage);

											setTimeout(() => {
												setSendModalOpen(false);
											}, 1200);
										} finally {
											setLoading(false);
											setSelectedTemplate(null);
										}
									}}
								>
									Enviar plantilla
								</PrimaryButton>
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<Button
										type="text"
										size="small"
										icon={<ReloadOutlined />}
										onClick={refreshList}
										loading={loading}
									/>
								</div>
								<Typography.Text type="secondary" style={{ marginLeft: 8 }}>
									Seleccionados: <b>{selectedCount}</b>
									{selectAllTotal > 0 ? (
										<>
											{" "}
											de <b>{selectAllTotal}</b>
										</>
									) : null}
								</Typography.Text>
								<FilterActions>
									<Select
										style={{ width: 200 }}
										value={sortLastConversation}
										onChange={(v) => setSortLastConversation(v)}
										options={[
											{
												value: "PDV",
												label: "Orden: nombre PDV",
											},
											{
												value: "DESC",
												label: "Orden: más recientes",
											},
											{
												value: "ASC",
												label: "Orden: más antiguas",
											},
										]}
									/>
								</FilterActions>
							</FilterContainer>
						)}
					</Space>

					<InboxShell>
						<ContactListPanel>
							<div className="contacts-header">
								<div className="contacts-title">
									<Typo level={6}>PDV / contactos</Typo>
									<Typography.Text type="secondary" style={{ fontSize: 12 }}>
										Seleccioná un contacto para ver la última conversación
									</Typography.Text>
								</div>
								<Button
									type="text"
									size="small"
									icon={<ReloadOutlined />}
									onClick={refreshList}
									loading={loading}
								/>
							</div>

							<div className="contacts-list">
								<List
									loading={loading}
									dataSource={rows}
									renderItem={(r) => {
										const selected =
											selectedPos?.agentRouteId === r.agentRouteId;
										const checked = selectedRowKeys.includes(r.agentRouteId);
										return (
											<ContactRow
												$isSelected={selected}
												$isDisabled={!r.pointOfSaleId || !r.lastMessagePreview}
												onClick={() => openDrawer(r)}
											>
												<div
													className="contact-select"
													onClick={(e) => e.stopPropagation()}
												>
													<Checkbox
														checked={checked}
														disabled={!r.pointOfSaleId}
														onChange={(e) => {
															const isChecked = e.target.checked;
															setSelectedRowKeys((prev) => {
																if (isChecked)
																	return prev.includes(r.agentRouteId)
																		? prev
																		: [...prev, r.agentRouteId];
																return prev.filter((x) => x !== r.agentRouteId);
															});
														}}
													/>
												</div>
												<div className="contact-body">
													<div className="contact-top">
														<Space size={6}>
															<Circle
																color={
																	r.isValidated
																		? COLORS.Success
																		: COLORS.Warning
																}
															/>
															<Typography.Text strong ellipsis>
																{r.pdvName || "PDV sin nombre"}
															</Typography.Text>
														</Space>
														<Typography.Text
															type="secondary"
															className="contact-time"
														>
															{r.lastMessageAt
																? moment
																		.utc(r.lastMessageAt)
																		.local()
																		.format("HH:mm")
																: "—"}
														</Typography.Text>
													</div>
													<Typography.Text
														type="secondary"
														className="contact-meta"
														ellipsis
													>
														{r.address || r.cuit || "Sin datos"}{" "}
														{r.province ? `• ${r.province}` : ""}{" "}
														{r.location ? `• ${r.location}` : ""}
													</Typography.Text>
													<div className="contact-preview">
														{r.lastMessagePreview || "Sin conversaciones"}
													</div>
													<Space wrap size={4}>
														{r.lastConversationPhase ? (
															<Tag
																color={
																	CONSTANTS.AGENT_CONVERSATION[
																		r.lastConversationPhase
																	]?.color || "blue"
																}
															>
																{CONSTANTS.AGENT_CONVERSATION[
																	r.lastConversationPhase
																]?.label || "Indefinido"}
															</Tag>
														) : null}
														<LeadStatusTag
															status={r.leadStatus}
															reason={r.leadStatusReason}
														/>
														{r.lastConversationHasTicket && (
															<Tag color="volcano">Ticket</Tag>
														)}
													</Space>
												</div>
											</ContactRow>
										);
									}}
								/>
							</div>

							{pageInfo && pageInfo.totalRecords > 0 && (
								<PanelPagination>
									<Button
										size="small"
										shape="circle"
										disabled={pageInfo.actualPage <= 1}
										onClick={() =>
											handleDecrement(pageInfo, fetchPage, setActualPage)
										}
									>
										<LeftOutlined />
									</Button>
									<AntdInput
										step={1}
										min={1}
										max={pageInfo.totalPages}
										onPressEnter={(e) =>
											handlePageChange(e, pageInfo, fetchPage, setActualPage)
										}
										onChange={(e) => setActualPage(e.target.value.toString())}
										value={actualPage.toString()}
										addonAfter={`/ ${pageInfo.totalPages}`}
										style={{ width: pageInfo.totalPages > 99 ? 76 : 66 }}
									/>
									<Button
										size="small"
										shape="circle"
										disabled={pageInfo.actualPage >= pageInfo.totalPages}
										onClick={() =>
											handleIncrement(pageInfo, fetchPage, setActualPage)
										}
									>
										<RightOutlined />
									</Button>
								</PanelPagination>
							)}
						</ContactListPanel>

						<ChatPanel>
							<ChatHeader>
								<ChatHeaderMeta style={{ flexDirection: "row", gap: 40 }}>
									<ChatHeaderMeta>
										<Typo level={6}>
											{getConversationDate(selectedConversation)}
										</Typo>
										<Typography.Text type="secondary" style={{ fontSize: 13 }}>
											{selectedPos?.pdvName || "Seleccioná un PDV"}
										</Typography.Text>
									</ChatHeaderMeta>
									{selectedConversation ? (
										<Space wrap size={6}>
											<ControlBadge
												$mode={getControlMode(selectedConversation)}
											>
												{getControlBadge(selectedConversation)}
											</ControlBadge>
											<Tag
												color={
													CONSTANTS.AGENT_CONVERSATION[
														selectedConversation.phase
													]?.color || "blue"
												}
											>
												{CONSTANTS.AGENT_CONVERSATION[
													selectedConversation.phase
												]?.label ||
													selectedConversation.phase ||
													"Fase"}
											</Tag>
											<LeadStatusTag
												status={selectedConversation.leadStatus}
												reason={selectedConversation.leadStatusReason}
											/>
											{selectedConversation.hasTicket && (
												<Tag color="volcano">Ticket</Tag>
											)}
										</Space>
									) : null}
									{/* {selectedConversation &&
											getControlMode(selectedConversation) === "Agent" &&
											!canTakeOverConversation(selectedConversation) && (
												<Typography.Text
													type="secondary"
													style={{ fontSize: 12 }}
												>
													Solo se puede tomar una conversación abierta o una
													cerrada con ticket.
												</Typography.Text>
											)} */}
								</ChatHeaderMeta>
								<Space wrap>
									<Button
										type="text"
										size="small"
										icon={<ReloadOutlined />}
										onClick={refreshMessagesOnly}
										loading={msgsLoading}
										disabled={!selectedConversationId}
									/>
									<DefaultButton
										onClick={() => setHistoryModalOpen(true)}
										disabled={!selectedPos}
										style={{ border: "1px solid #cbd5e1" }}
									>
										Historial
									</DefaultButton>
									{selectedConversation &&
										getControlMode(selectedConversation) === "Agent" &&
										canTakeOverConversation(selectedConversation) && (
											<PrimaryButton
												type="primary"
												onClick={() => setTakeoverModalOpen(true)}
												loading={controlActionLoading}
											>
												Tomar control
											</PrimaryButton>
										)}
									{selectedConversation &&
										getControlMode(selectedConversation) === "Human" && (
											<DefaultButton
												onClick={confirmReleaseToAgent}
												loading={controlActionLoading}
												style={{ border: "1px solid #cbd5e1" }}
											>
												Reactivar Agente
											</DefaultButton>
										)}
								</Space>
							</ChatHeader>

							<MessagesViewport ref={messagesViewportRef}>
								{msgInfo && msgPage < msgInfo.totalPages && (
									<div className="older-messages-action">
										<Button
											size="small"
											onClick={loadOlderMessages}
											loading={olderMsgsLoading}
										>
											Cargar mensajes anteriores
										</Button>
										<Typography.Text type="secondary" style={{ fontSize: 11 }}>
											Página {msgPage} de {msgInfo.totalPages}
										</Typography.Text>
									</div>
								)}
								<List
									loading={msgsLoading}
									dataSource={msgs}
									renderItem={(m) => {
										const source = getMessageSource(m);
										const isEvent = source === "System" || m.isEvent;
										if (isEvent)
											return (
												<TimelineEvent>
													<span>{m.content || "Evento de conversación"}</span>
													<small>{formatControlDate(m.created)}</small>
												</TimelineEvent>
											);
										const side = source === "EndUser" ? "left" : "right";
										const label =
											source === "Human"
												? "Humano" //m.senderUserId ||
												: source === "Agent"
													? "Agente"
													: "Cliente";
										return (
											<MessageBubble $side={side} $source={source}>
												<MessageMeta>
													<strong>{label}</strong>
													<span>{formatControlDate(m.created)}</span>
												</MessageMeta>
												<div className="bubble-content">{m.content || "—"}</div>
											</MessageBubble>
										);
									}}
								/>
							</MessagesViewport>

							<ComposerBar
								$disabled={getControlMode(selectedConversation) !== "Human"}
							>
								{getControlMode(selectedConversation) === "Human" ? (
									<>
										<Input.TextArea
											value={humanMessageText}
											onChange={(e) => setHumanMessageText(e.target.value)}
											placeholder="Escribí una respuesta manual..."
											autoSize={{ minRows: 1, maxRows: 4 }}
											onPressEnter={(e) => {
												if (!e.shiftKey) {
													e.preventDefault();
													sendHumanMessage();
												}
											}}
										/>
										<Button
											type="primary"
											shape="circle"
											icon={<SendOutlined />}
											onClick={sendHumanMessage}
											loading={sendingHumanMessage}
											disabled={sendingHumanMessage || !humanMessageText.trim()}
										/>
									</>
								) : (
									<Typography.Text type="secondary">
										Para responder manualmente es necesario tomar el control.
									</Typography.Text>
								)}
							</ComposerBar>
						</ChatPanel>
					</InboxShell>
					{/* </Card> */}
				</Col>

				{false && (
					<Drawer
						open={drawerOpen}
						width={1180}
						onClose={() => {
							setDrawerOpen(false);
							setSelectedPos(null);
							setHumanMessageText("");
						}}
						title={
							selectedPos
								? `Conversaciones • ${selectedPos.pdvName}`
								: "Conversaciones"
						}
					>
						<InboxShell>
							<ConversationSidebar>
								<SidebarHeader>
									<div>
										<Typo level={6}>Conversaciones</Typo>
										{/* <Typography.Text type="secondary" style={{ fontSize: 12 }}>
										{selectedPos?.pdvName || "Punto de venta"}
									</Typography.Text> */}
									</div>
									<Button
										type="text"
										size="small"
										icon={<ReloadOutlined />}
										onClick={refreshDrawer}
										loading={convLoading}
									/>
								</SidebarHeader>

								<CardContainer>
									<List
										loading={convLoading}
										dataSource={convs}
										renderItem={(c) => {
											const isSelected = c.id === selectedConversationId;
											const isHuman = getControlMode(c) === "Human";
											return (
												<ConversationRow
													$isSelected={isSelected}
													onClick={async () => {
														setSelectedConversationId(c.id);
														setHumanMessageText("");
														await loadMessages(c.id, 1);
													}}
												>
													<div className="row-top">
														<Typography.Text strong ellipsis>
															{selectedPos?.pdvName || "Conversación"}
														</Typography.Text>
														<Typography.Text type="secondary" className="time">
															{c.updatedAt
																? moment
																		.utc(c.updatedAt)
																		.local()
																		.format("HH:mm")
																: "—"}
														</Typography.Text>
													</div>
													<div className="row-preview">
														{c.lastMessagePreview || "Sin mensajes"}
													</div>
													<Space wrap size={4}>
														<ControlBadge $mode={getControlMode(c)}>
															{getControlBadge(c)}
														</ControlBadge>
														<Tag color={c.isFinished ? "default" : "green"}>
															{c.isFinished ? "Finalizada" : "Activa"}
														</Tag>
														<Tag
															color={
																CONSTANTS.AGENT_CONVERSATION[c.phase]?.color ||
																"blue"
															}
														>
															{CONSTANTS.AGENT_CONVERSATION[c.phase]?.label ||
																c.phase ||
																"Fase"}
														</Tag>
														<LeadStatusTag
															status={c.leadStatus}
															reason={c.leadStatusReason}
														/>
														{c.hasTicket && (
															<Tag color="volcano">Abrió Ticket</Tag>
														)}
													</Space>
													{isHuman && (
														<div className="human-meta">
															{c.humanAssigneeId
																? `Operador: ${c.humanAssigneeId}`
																: "Operador asignado"}
															{c.humanTakeoverAt
																? ` • Desde ${moment.utc(c.humanTakeoverAt).local().format("DD/MM HH:mm")}`
																: ""}
														</div>
													)}
												</ConversationRow>
											);
										}}
									/>
									{convInfo && (
										<CompaginationDiv
											style={{ marginTop: 10, marginBottom: 10 }}
										>
											<Button
												size="small"
												shape="circle"
												disabled={convInfo.actualPage <= 1}
												onClick={() =>
													loadConversations(
														selectedPos.pointOfSaleId,
														convPage - 1,
													)
												}
											>
												<LeftOutlined />
											</Button>
											<span>
												{convInfo.actualPage}/{convInfo.totalPages}
											</span>
											<Button
												size="small"
												shape="circle"
												disabled={convInfo.actualPage >= convInfo.totalPages}
												onClick={() =>
													loadConversations(
														selectedPos.pointOfSaleId,
														convPage + 1,
													)
												}
											>
												<RightOutlined />
											</Button>
										</CompaginationDiv>
									)}
								</CardContainer>
							</ConversationSidebar>

							<ChatPanel>
								<ChatHeader>
									<ChatHeaderMeta>
										<Typo level={6}>
											{selectedPos?.pdvName || "Seleccioná una conversación"}
										</Typo>
										<Space wrap size={6}>
											{selectedConversation ? (
												<>
													<ControlBadge
														$mode={getControlMode(selectedConversation)}
													>
														{getControlBadge(selectedConversation)}
													</ControlBadge>
													<Tag
														color={
															CONSTANTS.AGENT_CONVERSATION[
																selectedConversation.phase
															]?.color || "blue"
														}
													>
														{CONSTANTS.AGENT_CONVERSATION[
															selectedConversation.phase
														]?.label ||
															selectedConversation.phase ||
															"Fase"}
													</Tag>
													<LeadStatusTag
														status={selectedConversation.leadStatus}
														reason={selectedConversation.leadStatusReason}
													/>
												</>
											) : null}
										</Space>
										{selectedConversation?.humanAutoReturnEnabled && (
											<Typography.Text
												type="secondary"
												style={{ fontSize: 12 }}
											>
												Auto return after{" "}
												{selectedConversation.humanAutoReturnAfterMinutes}{" "}
												minutes of inactivity. Last human activity:{" "}
												{formatControlDate(
													selectedConversation.humanLastActivityAt ||
														selectedConversation.humanTakeoverAt,
												)}
											</Typography.Text>
										)}
										{getControlMode(selectedConversation) === "Human" && (
											<Typography.Text
												type="secondary"
												style={{ fontSize: 12 }}
											>
												{selectedConversation?.humanAssigneeId
													? `Operador: ${selectedConversation.humanAssigneeId}`
													: "Operador asignado"}
												{selectedConversation?.humanTakeoverAt
													? ` • Toma: ${formatControlDate(selectedConversation.humanTakeoverAt)}`
													: ""}
											</Typography.Text>
										)}
									</ChatHeaderMeta>
									<Space>
										<Button
											type="text"
											size="small"
											icon={<ReloadOutlined />}
											onClick={refreshMessagesOnly}
											loading={msgsLoading}
											disabled={!selectedConversationId}
										/>
										{selectedConversation &&
											getControlMode(selectedConversation) === "Agent" && (
												<PrimaryButton
													type="primary"
													onClick={() => setTakeoverModalOpen(true)}
													loading={controlActionLoading}
												>
													Tomar control
												</PrimaryButton>
											)}
										{selectedConversation &&
											getControlMode(selectedConversation) === "Human" && (
												<DefaultButton
													onClick={confirmReleaseToAgent}
													loading={controlActionLoading}
													style={{ border: "1px solid #cbd5e1" }}
												>
													Modo agente
												</DefaultButton>
											)}
									</Space>
								</ChatHeader>

								<MessagesViewport>
									<List
										loading={msgsLoading}
										dataSource={msgs}
										renderItem={(m) => {
											const source = getMessageSource(m);
											const isEvent = source === "System" || m.isEvent;
											if (isEvent) {
												return (
													<TimelineEvent>
														<span>{m.content || "Evento de conversación"}</span>
														<small>{formatControlDate(m.created)}</small>
													</TimelineEvent>
												);
											}

											const side = source === "EndUser" ? "left" : "right";
											const label =
												source === "Human"
													? m.senderUserId || "Human"
													: source === "Agent"
														? "Agent"
														: "Cliente";

											return (
												<MessageBubble $side={side} $source={source}>
													<MessageMeta>
														<strong>{label}</strong>
														<span>{formatControlDate(m.created)}</span>
													</MessageMeta>
													<div className="bubble-content">
														{m.content || "—"}
													</div>
												</MessageBubble>
											);
										}}
									/>
									{msgInfo && (
										<CompaginationDiv
											style={{ marginTop: 10, marginBottom: 10 }}
										>
											<Button
												size="small"
												shape="circle"
												disabled={msgInfo.actualPage <= 1}
												onClick={() =>
													loadMessages(selectedConversationId, msgPage - 1)
												}
											>
												<LeftOutlined />
											</Button>
											<span>
												{msgInfo.actualPage}/{msgInfo.totalPages}
											</span>
											<Button
												size="small"
												shape="circle"
												disabled={msgInfo.actualPage >= msgInfo.totalPages}
												onClick={() =>
													loadMessages(selectedConversationId, msgPage + 1)
												}
											>
												<RightOutlined />
											</Button>
										</CompaginationDiv>
									)}
								</MessagesViewport>

								<ComposerBar
									$disabled={getControlMode(selectedConversation) !== "Human"}
								>
									{getControlMode(selectedConversation) === "Human" ? (
										<>
											<Input.TextArea
												value={humanMessageText}
												onChange={(e) => setHumanMessageText(e.target.value)}
												placeholder="Escribí una respuesta manual..."
												autoSize={{ minRows: 1, maxRows: 4 }}
												onPressEnter={(e) => {
													if (!e.shiftKey) {
														e.preventDefault();
														sendHumanMessage();
													}
												}}
											/>
											<Button
												type="primary"
												shape="circle"
												icon={<SendOutlined />}
												onClick={sendHumanMessage}
												loading={sendingHumanMessage}
												disabled={
													sendingHumanMessage || !humanMessageText.trim()
												}
											/>
										</>
									) : (
										<Typography.Text type="secondary">
											Para responder manualmente se debe tomar el control.
										</Typography.Text>
									)}
								</ComposerBar>
							</ChatPanel>
						</InboxShell>
					</Drawer>
				)}
			</Row>

			<Modal
				open={historyModalOpen}
				title="Historial de conversaciones"
				onCancel={() => setHistoryModalOpen(false)}
				footer={null}
				width={720}
			>
				<HistoryModalBody>
					<HistoryListViewport>
						<List
							loading={convLoading}
							dataSource={convs}
							renderItem={(c) => {
								const isSelected = c.id === selectedConversationId;
								const isHuman = getControlMode(c) === "Human";
								return (
									<ConversationRow
										$isSelected={isSelected}
										onClick={async () => {
											setSelectedConversationId(c.id);
											setHumanMessageText("");
											await loadMessages(c.id, 1);
											setHistoryModalOpen(false);
										}}
									>
										<div className="row-top">
											<Typography.Text strong>
												{getConversationDate(c)}
											</Typography.Text>
											<Typography.Text type="secondary" className="time">
												{c.updatedAt
													? moment.utc(c.updatedAt).local().format("HH:mm")
													: "—"}
											</Typography.Text>
										</div>
										<Typography.Text type="secondary" className="pdv-name">
											{selectedPos?.pdvName || "PDV"}
										</Typography.Text>
										<div className="row-preview">
											{c.lastMessagePreview || "Sin mensajes"}
										</div>
										<Space wrap size={4}>
											<ControlBadge $mode={getControlMode(c)}>
												{getControlBadge(c)}
											</ControlBadge>
											<Tag color={c.isFinished ? "default" : "green"}>
												{c.isFinished ? "Finalizada" : "Activa"}
											</Tag>
											<Tag
												color={
													CONSTANTS.AGENT_CONVERSATION[c.phase]?.color || "blue"
												}
											>
												{CONSTANTS.AGENT_CONVERSATION[c.phase]?.label ||
													c.phase ||
													"Fase"}
											</Tag>
											<LeadStatusTag
												status={c.leadStatus}
												reason={c.leadStatusReason}
											/>
											{c.hasTicket && <Tag color="volcano">Ticket</Tag>}
										</Space>
										{isHuman && (
											<div className="human-meta">
												{c.humanAssigneeId
													? `Operador: ${c.humanAssigneeId}`
													: "Operador asignado"}
												{c.humanTakeoverAt
													? ` • Desde ${moment.utc(c.humanTakeoverAt).local().format("DD/MM HH:mm")}`
													: ""}
											</div>
										)}
									</ConversationRow>
								);
							}}
						/>
					</HistoryListViewport>
					{convInfo && (
						<HistoryPagination>
							<Button
								size="small"
								shape="circle"
								disabled={convInfo.actualPage <= 1}
								onClick={() =>
									loadConversations(selectedPos.pointOfSaleId, convPage - 1, {
										keepSelection: true,
										keepMsgPage: true,
									})
								}
							>
								<LeftOutlined />
							</Button>
							<span>
								{convInfo.actualPage}/{convInfo.totalPages}
							</span>
							<Button
								size="small"
								shape="circle"
								disabled={convInfo.actualPage >= convInfo.totalPages}
								onClick={() =>
									loadConversations(selectedPos.pointOfSaleId, convPage + 1, {
										keepSelection: true,
										keepMsgPage: true,
									})
								}
							>
								<RightOutlined />
							</Button>
						</HistoryPagination>
					)}
				</HistoryModalBody>
			</Modal>

			<Modal
				open={takeoverModalOpen}
				title="Tomar control conversacion"
				onCancel={() => {
					setTakeoverModalOpen(false);
					setTakeoverReason("");
				}}
				onOk={handleTakeover}
				confirmLoading={controlActionLoading}
				okText="Tomar control"
				cancelText="Cancelar"
			>
				<Space direction="vertical" style={{ width: "100%" }} size={8}>
					<Typography.Text type="secondary">
						El servidor avisará al cliente que un humano está atendiendo la
						conversación.
					</Typography.Text>
					<Input.TextArea
						value={takeoverReason}
						onChange={(e) => setTakeoverReason(e.target.value)}
						placeholder="Motivo opcional. Ej: El cliente necesita ayuda de un humano"
						autoSize={{ minRows: 3, maxRows: 5 }}
					/>
				</Space>
			</Modal>

			<Modal
				open={sendModalOpen}
				title="Enviando plantilla"
				closable={false}
				maskClosable={false}
				footer={null}
			>
				<Space direction="vertical" style={{ width: "100%" }} size={12}>
					<Typography.Text>{sendStatus}</Typography.Text>

					<Progress percent={sendProgress} />

					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{sendDetails}
					</Typography.Text>

					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						No cierres esta ventana. El envío puede demorar según la cantidad de
						destinatarios.
					</Typography.Text>
				</Space>
			</Modal>

			<Modal
				open={templatePreviewOpen}
				onCancel={() => setTemplatePreviewOpen(false)}
				title="Vista previa de plantilla"
				footer={null}
			>
				{(() => {
					const t = templateMap[selectedTemplate];
					if (!t) return <Typography.Text type="secondary">—</Typography.Text>;

					return (
						<Space direction="vertical" style={{ width: "100%" }}>
							<Space wrap>
								<Tag>Categoría: {t.category}</Tag>
								<Tag>Idioma: {t.language}</Tag>
								{t.headerFormat ? <Tag>Header: {t.headerFormat}</Tag> : null}
								<Tag color={t.status === "APPROVED" ? "green" : "orange"}>
									Estado: {t.status}
								</Tag>
							</Space>
							<div style={{ marginTop: 5, marginBottom: 5 }}>
								<Typography.Text strong>Nombre clave: {t.name}</Typography.Text>
							</div>
							{isImageHeaderTemplate(t) ? (
								<img
									src={t.headerMediaUrl}
									alt="Header de plantilla"
									style={{
										width: "100%",
										maxHeight: 260,
										objectFit: "cover",
										borderRadius: 8,
										border: "1px solid #eee",
									}}
								/>
							) : null}
							<div
								style={{
									background: "#fafafa",
									border: "1px solid #eee",
									borderRadius: 8,
									padding: 12,
									whiteSpace: "pre-wrap",
									fontFamily: "monospace",
								}}
							>
								{getPreviewText(t)}
							</div>
						</Space>
					);
				})()}
			</Modal>
		</Inner>
	);
}
